import axios, { AxiosRequestConfig } from 'axios';
import { Sitter } from '../types/Sitter';
import { DataResponse, PagedResponse } from '../types/api';

const URL = '/v2/sitters';
const INTERNAL_URL = '/internal/sitters';
const APPROVAL_EMAIL_URL = '/v2/sitter-approval-email';
const AVAILABILTIES_URL = '/v2/sitter-availabilities';
const HIDDEN_REASONS_URL = '/v2/sitter-hidden-reasons';
const PROFILE_STATUS_URL = '/v2/sitter-profile-status';
const CERTIFICATIONS_URL = '/v2/certifications';
const SCHEDULES_URL = '/v2/sitter-schedules';

class SitterService {
  static get(
    id: number,
    config?: AxiosRequestConfig
  ): Promise<DataResponse<Sitter>> {
    return axios.get(`${URL}/${id}`, config);
  }

  static getByFilter(
    config?: AxiosRequestConfig
  ): Promise<PagedResponse<Sitter>> {
    return axios.get(URL, config);
  }

  static getInternal(config: AxiosRequestConfig = {}) {
    if (typeof window === 'undefined') {
      config.baseURL = `http://${process.env.US_SERVICES_HOST}:${process.env.US_SERVICES_PORT}`;
    }

    return config.params.sitterId
      ? axios.get(`${INTERNAL_URL}/${config.params.sitterId}`, config)
      : axios.get(INTERNAL_URL, config);
  }

  static getAvailabilities(config?: AxiosRequestConfig) {
    return axios.get(AVAILABILTIES_URL, config);
  }

  static getCertifications(id: number, config?: AxiosRequestConfig) {
    return axios.get(`${CERTIFICATIONS_URL}/${id}`, config);
  }

  static getHiddenReasons(config?: AxiosRequestConfig) {
    return axios.get(HIDDEN_REASONS_URL, config);
  }

  static getProfileStatus(id: number, config?: AxiosRequestConfig) {
    return axios.get(`${PROFILE_STATUS_URL}/${id}`, config);
  }

  static getSchedules(config?: AxiosRequestConfig) {
    return axios.get(SCHEDULES_URL, config);
  }

  static getStats(id: number, config?: AxiosRequestConfig) {
    return axios.get(`${URL}/${id}/stats`, config);
  }

  static patch(
    id: number,
    data: Partial<Sitter>,
    config?: AxiosRequestConfig
  ): Promise<DataResponse<Sitter>> {
    return axios.patch(`${URL}/${id}`, data, config);
  }

  // TODO: create the somewhat complex type for the availabilityData parameter
  static patchAvailabilities(availabilityData: unknown, config?: AxiosRequestConfig): Promise<DataResponse<unknown>> {
    return axios.patch(AVAILABILTIES_URL, availabilityData, config);
  }

  // Day-based POST: replace the set of times for a given day.
  // Body: { sitterId, date: "YYYY-MM-DD 00:00:00", times: [{ start, end }],
  //         pastMidnight?: boolean, until?: "YYYY-MM-DD HH:MM:SS" }
  static saveAvailabilities(availabilityData: unknown, config?: AxiosRequestConfig): Promise<DataResponse<unknown>> {
    return axios.post(AVAILABILTIES_URL, availabilityData, config);
  }

  // Day-based DELETE: remove all availability entries matching the query params.
  static deleteAvailabilities(config?: AxiosRequestConfig): Promise<DataResponse<unknown>> {
    return axios.delete(AVAILABILTIES_URL, config);
  }

  // PATCH /v2/sitters/:sitterId/stats/:statsId
  // Angular `SitterStatsService.webservice.update(data, ...)` sends the full
  // stats payload — match that. Typical body: { id, sitterId, lastCalendarUpdate: { last } }.
  static patchStats(
    sitterId: number,
    statsId: number,
    body?: unknown,
    config?: AxiosRequestConfig,
  ): Promise<DataResponse<unknown>> {
    return axios.patch(`${URL}/${sitterId}/stats/${statsId}`, body, config);
  }

  static sendApprovalEmail(sitterId: number, config?: AxiosRequestConfig): Promise<DataResponse<unknown>> {
    return axios.post(`${APPROVAL_EMAIL_URL}/${sitterId}`, undefined, config);
  }
}

export { SitterService };
